/**
 * Thin typed fetch client over @windrop/contracts.
 *
 * TODO(api-client): swap for `@windrop/api-client` once docs/modules/api-client.md lands. Keep the surface
 * (`request`, `ApiError`, `apiEvents`) so call sites in `lib/api/admin.ts` are the only thing to touch.
 *
 * - Cookies carry the staff session (`credentials: 'include'`), never a token in storage or URLs (§17.3).
 * - Mutations send an `Idempotency-Key` (§15.1) unless the caller supplies one.
 * - Errors are RFC 9457 problem documents (`ProblemDetails`); `ApiError.code` is the catalogue code.
 * - A 401 anywhere raises `apiEvents` `unauthenticated`, which the session store listens to.
 */
import {
  IDEMPOTENCY_KEY_HEADER,
  ProblemDetails,
  problemCodeFromType,
  type ProblemCode,
} from '@windrop/contracts';
import { readRuntimeConfig } from '../runtime-config';

export type ApiEvent = 'unauthenticated' | 'step_up_required';
type Listener = (event: ApiEvent, error: ApiError) => void;

const listeners = new Set<Listener>();
export const apiEvents = {
  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
  emit(event: ApiEvent, error: ApiError): void {
    for (const l of listeners) l(event, error);
  },
};

export class ApiError extends Error {
  readonly status: number;
  readonly problem: ProblemDetails | null;
  readonly code: ProblemCode | undefined;
  readonly supportReference: string | undefined;
  constructor(status: number, problem: ProblemDetails | null, fallbackMessage: string) {
    super(problem?.detail ?? problem?.title ?? fallbackMessage);
    this.name = 'ApiError';
    this.status = status;
    this.problem = problem;
    this.code = problem ? problemCodeFromType(problem.type) : undefined;
    this.supportReference = problem?.supportReference;
  }
  /** Field pointer (`/config/identity/slug`) → message, for form error summaries. */
  fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const e of this.problem?.errors ?? []) out[e.pointer.replace(/^#/, '')] = e.message;
    return out;
  }
}

export const isApiError = (e: unknown): e is ApiError => e instanceof ApiError;

export type QueryValue = string | number | boolean | undefined | null;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, QueryValue>;
  /** `true` (default for mutations) generates a key; a string reuses one for safe retries; `false` omits it. */
  idempotencyKey?: boolean | string;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

/**
 * CSRF token for cookie-authenticated mutations (D-702). The API returns it in every session response (`csrf`) and
 * also sets a readable `windrop_csrf` cookie — but that cookie lives on the API host, which the console cannot read
 * when the two run on different hostnames (api.* vs console.*), so the returned token wins.
 */
let csrfToken: string | null = null;
export const setCsrfToken = (token: string | null | undefined): void => {
  if (token) csrfToken = token;
};
export const clearCsrfToken = (): void => {
  csrfToken = null;
};

export const readCookie = (name: string): string | null => {
  if (typeof document === 'undefined') return null;
  for (const part of document.cookie.split(';')) {
    const [k, ...v] = part.trim().split('=');
    if (k === name) return decodeURIComponent(v.join('='));
  }
  return null;
};

const newIdempotencyKey = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${performance.now().toString(36)}`;

export const buildUrl = (path: string, query?: Record<string, QueryValue>): string => {
  const base = readRuntimeConfig().apiOrigin;
  const url = `${base}${path.startsWith('/') ? path : `/${path}`}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '') continue;
    params.set(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
};

async function parseProblem(res: Response): Promise<ProblemDetails | null> {
  const type = res.headers.get('content-type') ?? '';
  if (!type.includes('json')) return null;
  try {
    const parsed = ProblemDetails.safeParse(await res.json());
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const method = opts.method ?? 'GET';
  const headers: Record<string, string> = { Accept: 'application/json', ...opts.headers };
  const init: RequestInit = { method, credentials: 'include', signal: opts.signal, headers };
  if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(opts.body);
  }
  if (method !== 'GET') {
    const key = opts.idempotencyKey ?? true;
    if (key !== false) headers[IDEMPOTENCY_KEY_HEADER] = key === true ? newIdempotencyKey() : key;
    // Double-submit CSRF (D-702/D-801): the API sets a readable `windrop_csrf` cookie beside the HttpOnly session.
    const csrf = csrfToken ?? readCookie('windrop_csrf');
    if (csrf && !headers['x-csrf']) headers['x-csrf'] = csrf;
  }
  const res = await fetch(buildUrl(path, opts.query), init);
  if (res.ok) {
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }
  const error = new ApiError(res.status, await parseProblem(res), `${method} ${path} failed (${res.status})`);
  if (res.status === 401) apiEvents.emit('unauthenticated', error);
  if (error.code === 'step_up_required') apiEvents.emit('step_up_required', error);
  throw error;
}

export const api = {
  get: <T>(path: string, query?: Record<string, QueryValue>, opts?: RequestOptions) =>
    request<T>(path, { ...opts, method: 'GET', query }),
  post: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>(path, { ...opts, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>(path, { ...opts, method: 'PATCH', body }),
  delete: <T>(path: string, opts?: RequestOptions) => request<T>(path, { ...opts, method: 'DELETE' }),
};
